import { GameState } from '../../core/GameState.js';
/**
 * PixiRenderer.js - WebGL Rendering Orchestrator using PixiJS
 *
 * Main rendering system that coordinates all PixiJS rendering:
 * - PIXI.Application setup and WebGL context management
 * - Layer/container management
 * - Delegating to specialized renderers
 * - Texture management
 */

import * as PIXI from 'pixi.js';
import { showNotification } from '../../client.js';
import { ClientLogger } from '../../logger/ClientLogger.js';
import { PixiCamera } from './PixiCamera.js';
import { PixiEffectsRenderer } from './PixiEffectsRenderer.js';
import { PixiEntityRenderer } from './PixiEntityRenderer.js';
import { PixiHUDRenderer } from './PixiHUDRenderer.js';
import { PixiTrailRenderer } from './PixiTrailRenderer.js';
import { PixiContainerRenderer } from './PixiContainerRenderer.js';
import { PixiWorldRenderer } from './PixiWorldRenderer.js';
import { getWorldTheme } from './worldTheme.js';

const pixiRendererLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const pixiRendererLogger = ClientLogger.getLogger('renderer') || pixiRendererLoggerFallback;

const PixiRenderer = {
  // PixiJS application instance
  app: null,

  // Layer containers (z-order from bottom to top)
  layers: {
    grid: null,         // Background grid (screen space)
    world: null,        // World container (transformed by camera)
    tiles: null,        // Tiled tile layers (ground/floor)
    walls: null,        // Wall boundaries
    objects: null,      // World objects
    zoneOverlay: null,  // Zone overlay shading
    trails: null,       // Teleport trails
    entities: null,     // Players container
    projectiles: null,  // Projectiles
    damageNumbers: null,// Floating damage numbers
    hud: null           // HUD overlay (screen space)
  },

  // Sub-renderers
  worldRenderer: null,
  entityRenderer: null,

  // Texture cache
  textures: new Map(),

  // Configuration
  backgroundColor: 0x1a4d2e, // Default: Dark green for grass maps
  currentMapBackgroundColor: 0x1a4d2e, // Track current background color

  // Is renderer initialized?
  initialized: false,

  // Context state tracking
  contextLost: false,

  // Event handler references (for cleanup)
  _resizeHandler: null,
  _contextLostHandler: null,
  _contextRestoredHandler: null,

  // Stats for performance comparison
  stats: {
    frameCount: 0,
    lastFpsUpdate: 0,
    fps: 0
  },

  /**
   * Initialize the PixiJS renderer
   * @param {HTMLElement} container - Container element to append canvas to
   * @returns {boolean} - True if initialization successful
   */
  /**
   * @param {HTMLElement} container
   * @param {{ send?: (event: string, payload: object) => void }} [services] -
   *   the client-side services renderers may call. `send` is the only one today:
   *   the HUD's inventory drag/drop and cosmetics picker send protocol messages
   *   through it. Renderers never reach for window.* to find these.
   */
  async init(container, services = {}) {
    if (this.initialized) return true;
    this.services = services;

    // Check if PixiJS is available
    if (typeof PIXI === 'undefined') {
      pixiRendererLogger.warn('PixiJS not loaded, WebGL renderer unavailable');
      return false;
    }

    try {
      // Create PixiJS application
      this.app = new PIXI.Application();

      // Initialize with WebGL, fallback to Canvas
      await this.app.init({
        width: window.innerWidth,
        height: window.innerHeight,
        backgroundColor: this.backgroundColor,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
        powerPreference: 'high-performance',
        roundPixels: false  // Disable pixel rounding for smoother movement
      });

      // Add canvas to container
      container.appendChild(this.app.canvas);

      // Style the canvas
      this.app.canvas.style.position = 'absolute';
      this.app.canvas.style.top = '0';
      this.app.canvas.style.left = '0';
      this.app.canvas.style.zIndex = '1';
      this.app.canvas.style.cursor = 'auto';

      // Make canvas focusable for keyboard input (important for Tab key handling)
      this.app.canvas.tabIndex = 0;
      this.app.canvas.style.outline = 'none'; // Hide focus outline

      // Focus canvas when game starts (helps with keyboard input in Edge)
      this.app.canvas.focus();

      // Re-focus canvas when clicked (in case focus was lost)
      this.app.canvas.addEventListener('click', () => {
        this.app.canvas.focus();
      });

      // Re-focus canvas when mouse enters (more aggressive focus capture)
      this.app.canvas.addEventListener('mouseenter', () => {
        if (document.activeElement !== this.app.canvas) {
          this.app.canvas.focus();
        }
      });

      // Create layer hierarchy
      this._createLayers();

      // Initialize camera
      PixiCamera.init(
        this.layers.world,
        this.app.screen.width,
        this.app.screen.height
      );

      // Initialize sub-renderers
      PixiWorldRenderer.init(
        this.layers.grid,
        this.layers.walls,
        this.layers.objects,
        this.layers.tiles,
        null,
        this.layers.zoneOverlay
      );

      PixiEntityRenderer.init(
        this.layers.entities,
        this.layers.projectiles
      );

      // Initialize effects renderer
      PixiEffectsRenderer.init(
        this.layers.trails,
        this.layers.damageNumbers
      );

      // Movement trails share the trails layer (below entities)
      PixiTrailRenderer.init(this.layers.trails);

      // Loot containers as flat shapes, on the objects layer above the static world shapes
      PixiContainerRenderer.init(this.layers.objects);

      // Initialize HUD renderer
      console.log('[PixiRenderer] HUD Init dimensions - app.screen:', this.app.screen.width, 'x', this.app.screen.height);
      console.log('[PixiRenderer] Canvas internal:', this.app.canvas.width, 'x', this.app.canvas.height);
      console.log('[PixiRenderer] Window inner:', window.innerWidth, 'x', window.innerHeight);
      PixiHUDRenderer.init(
        this.layers.hud,
        this.app.screen.width,
        this.app.screen.height,
        services
      );

      // Handle resize (store reference for cleanup)
      this._resizeHandler = () => this.handleResize();
      window.addEventListener('resize', this._resizeHandler);

      // Handle WebGL context loss/restore
      this._contextLostHandler = (e) => this._handleContextLost(e);
      this._contextRestoredHandler = () => this._handleContextRestored();
      this.app.canvas.addEventListener('webglcontextlost', this._contextLostHandler, false);
      this.app.canvas.addEventListener('webglcontextrestored', this._contextRestoredHandler, false);

      this.initialized = true;
      this.contextLost = false;
      pixiRendererLogger.info('PixiJS WebGL Renderer initialized');
      pixiRendererLogger.info(`   Renderer: ${this.app.renderer.type === 1 ? 'WebGL' : 'Canvas'}`);

      return true;
    } catch (error) {
      pixiRendererLogger.error('Failed to initialize PixiJS:', error);
      return false;
    }
  },

  /**
   * Create the layer hierarchy
   * @private
   */
  _createLayers() {
    const stage = this.app.stage;

    // World container (transformed by camera)
    this.layers.world = new PIXI.Container();
    stage.addChild(this.layers.world);

    // Layers inside world container (z-order from bottom to top)
    // Tiles layer first (bottom) - for Tiled ground tiles
    this.layers.tiles = new PIXI.Container();
    this.layers.world.addChild(this.layers.tiles);

    // Grid layer (above tiles, in world space so it scrolls with map)
    this.layers.grid = new PIXI.Container();
    this.layers.world.addChild(this.layers.grid);

    this.layers.walls = new PIXI.Container();
    this.layers.world.addChild(this.layers.walls);

    this.layers.objects = new PIXI.Container();
    this.layers.world.addChild(this.layers.objects);

    this.layers.zoneOverlay = new PIXI.Container();
    this.layers.world.addChild(this.layers.zoneOverlay);

    this.layers.trails = new PIXI.Container();
    this.layers.world.addChild(this.layers.trails);

    this.layers.entities = new PIXI.Container();
    this.layers.world.addChild(this.layers.entities);

    this.layers.projectiles = new PIXI.Container();
    this.layers.world.addChild(this.layers.projectiles);

    this.layers.damageNumbers = new PIXI.Container();
    this.layers.world.addChild(this.layers.damageNumbers);

    // HUD layer (screen space, above everything)
    this.layers.hud = new PIXI.Container();
    stage.addChild(this.layers.hud);
  },

  /**
   * Handle window resize
   */
  handleResize() {
    if (!this.app || this.contextLost) return;

    const width = window.innerWidth;
    const height = window.innerHeight;

    this.app.renderer.resize(width, height);
    PixiCamera.resize(width, height);
    PixiHUDRenderer.resize(width, height);
  },

  /**
   * Handle WebGL context loss
   * @param {Event} e - WebGL context lost event
   * @private
   */
  _handleContextLost(e) {
    e.preventDefault(); // Allow context to be restored
    this.contextLost = true;
    pixiRendererLogger.error('[PixiRenderer] WebGL context lost! Waiting for restore...');

    // Notify user
    if (typeof showNotification === 'function') {
      showNotification('Graphics context lost - attempting recovery...', 'warning');
    }
  },

  /**
   * Handle WebGL context restore
   * @private
   */
  _handleContextRestored() {
    pixiRendererLogger.info('[PixiRenderer] WebGL context restored!');
    this.contextLost = false;

    // Clear texture cache - textures are invalid after context loss
    for (const texture of this.textures.values()) {
      texture.destroy(true);
    }
    this.textures.clear();

    // Reset wall drawing flag to redraw on next frame
    this._wallsDrawn = false;

    // Reset sub-renderers to recreate their graphics
    if (PixiEntityRenderer?.reset) PixiEntityRenderer.reset();
    if (PixiWorldRenderer?.reset) PixiWorldRenderer.reset();
    if (PixiEffectsRenderer?.reset) PixiEffectsRenderer.reset();
    if (PixiTrailRenderer?.reset) PixiTrailRenderer.reset();
    if (PixiContainerRenderer?.reset) PixiContainerRenderer.reset();
    if (PixiHUDRenderer?.reset) PixiHUDRenderer.reset();

    // Notify user
    if (typeof showNotification === 'function') {
      showNotification('Graphics recovered!', 'success');
    }
  },

  /**
   * Main render function - called every frame
   * @param {Object} renderContext - Context containing all render state
   */
  render(renderContext) {
    if (!this.initialized || this.contextLost) return;

    const { currentTime, renderTime, localRenderState, gameState, callbacks } = renderContext;

    // Update FPS stats
    this._updateStats(currentTime);

    // Update camera position
    PixiCamera.follow(localRenderState.x, localRenderState.y);

    // Update grid (screen space with parallax)
    PixiWorldRenderer.drawGrid(
      localRenderState.x,
      localRenderState.y,
      this.app.screen.width,
      this.app.screen.height
    );

    // Update walls (only needs to be done once or when map changes)
    if (!this._wallsDrawn) {
      PixiWorldRenderer.drawWalls(gameState.mapWidth, gameState.mapHeight);
      this._wallsDrawn = true;
    }

    // Update world objects
    PixiWorldRenderer.updateWorldObjects(gameState.worldObjects, {
      getRenderable: (obj) => {
        if (obj.objectType === 'lootContainer') return null; // drawn by PixiContainerRenderer
        const renderable = callbacks.getWorldObjectRenderData?.(obj);
        if (renderable) {
          return renderable;
        }
        const fallbackImage = callbacks.getWorldObjectImage?.(obj);
        return fallbackImage ? { type: 'static', image: fallbackImage } : null;
      },
      getTextureForStatic: (image) => this.getTextureFromImage(image),
      getTextureForFrame: (renderable) => this.getTextureFromFrame(renderable)
    });

    PixiContainerRenderer.update(gameState);

    PixiWorldRenderer.updateZoneOverlay(gameState.zoneView(), gameState.mapWidth, gameState.mapHeight);

    // Debug collision visualization
    PixiWorldRenderer.drawDebugCollisions(gameState.worldObjects);

    // Update players
    PixiEntityRenderer.updatePlayers(renderTime, gameState, {
      getSkinTexture: (skinId) => {
        const img = callbacks.getSkinImage?.(skinId);
        return img ? this.getTextureFromImage(img) : null;
      },
      getWeaponTexture: (path) => {
        const img = callbacks.getWeaponSprite?.(path);
        return img ? this.getTextureFromImage(img) : null;
      },
      getBackpackTexture: (backpackId) => {
        const img = callbacks.getBackpackImage?.(backpackId);
        return img ? this.getTextureFromImage(img) : null;
      },
      getPlayerLastShotTime: callbacks.getPlayerLastShotTime,
      debugMuzzleOffset: callbacks.debugMuzzleOffset
    });

    // Decoys: same call, same callbacks, same sprite groups as the players above
    PixiEntityRenderer.updateDecoys(renderTime, gameState, {
      getSkinTexture: (skinId) => {
        const img = callbacks.getSkinImage?.(skinId);
        return img ? this.getTextureFromImage(img) : null;
      },
      getWeaponTexture: (path) => {
        const img = callbacks.getWeaponSprite?.(path);
        return img ? this.getTextureFromImage(img) : null;
      },
      getBackpackTexture: (backpackId) => {
        const img = callbacks.getBackpackImage?.(backpackId);
        return img ? this.getTextureFromImage(img) : null;
      },
      getPlayerLastShotTime: callbacks.getPlayerLastShotTime,
      debugMuzzleOffset: callbacks.debugMuzzleOffset
    });

    // Update projectiles
    PixiEntityRenderer.updateProjectiles(gameState, {
      INTERPOLATION_DELAY: callbacks.INTERPOLATION_DELAY,
      getProjectileSprite: callbacks.getProjectileSprite
    });

    // Movement trails: sample rendered positions, draw per style
    PixiTrailRenderer.update(renderTime, performance.now(), gameState);

    // Update teleport trails
    PixiEffectsRenderer.updateTeleportTrails(gameState.teleportTrails || [], {
      getSkinTexture: (skinId) => {
        const img = callbacks.getSkinImage?.(skinId);
        return img ? this.getTextureFromImage(img) : null;
      },
      getTeleportTrailConfig: callbacks.getTeleportTrailConfig
    });

    PixiEffectsRenderer.updateNullifyRings(gameState.nullifyRings || []);

    // Update floating damage numbers
    PixiEffectsRenderer.updateFloatingDamageNumbers(gameState.floatingDamageNumbers);

    PixiEffectsRenderer.updateShotStreaks(gameState.shotStreaks);
    PixiEffectsRenderer.updatePredictedImpacts(gameState.predictedImpacts);
    PixiEffectsRenderer.updateHitregMarks(gameState.hitregMarks, gameState.predictedImpacts, GameState.DEBUG_MODE && gameState.hitregDebugActive);

    // Update HUD
    PixiHUDRenderer.update(gameState, {
      getAbilityIcon: (abilityId) => {
        const img = callbacks.getAbilityIcon?.(abilityId);
        return img ? this.getTextureFromImage(img) : null;
      },
      getWeaponIcon: (iconPath) => {
        const img = callbacks.getWeaponIcon?.(iconPath);
        return img ? this.getTextureFromImage(img) : null;
      },
      getAbility: callbacks.getAbility,
      uiConfig: callbacks.uiConfig,
      gameModeConfig: callbacks.gameModeConfig,
      DEBUG_MODE: callbacks.DEBUG_MODE,
      mouseWorldX: callbacks.mouseWorldX,
      mouseWorldY: callbacks.mouseWorldY,
      fps: this.getFPS(),
      ping: callbacks.ping || 0,
      netStats: callbacks.netStats || null,
      rendererType: this.getRendererType()
    });
  },

  /**
   * Update performance stats
   * @private
   */
  _updateStats(currentTime) {
    this.stats.frameCount++;

    if (currentTime - this.stats.lastFpsUpdate >= 1000) {
      this.stats.fps = this.stats.frameCount;
      this.stats.frameCount = 0;
      this.stats.lastFpsUpdate = currentTime;
    }
  },

  /**
   * Get texture from URL (cached)
   * @param {string} url - Texture URL
   * @returns {PIXI.Texture|null}
   */
  getTexture(url) {
    if (!url) return null;

    if (this.textures.has(url)) {
      return this.textures.get(url);
    }

    try {
      const texture = PIXI.Texture.from(url);
      this.textures.set(url, texture);
      return texture;
    } catch (e) {
      pixiRendererLogger.warn('Failed to load texture:', url);
      return null;
    }
  },

  /**
   * Get texture from Image element (cached)
   * @param {HTMLImageElement} img - Image element
   * @returns {PIXI.Texture|null}
   */
  getTextureFromImage(img) {
    if (!img || !img.src) return null;

    if (this.textures.has(img.src)) {
      return this.textures.get(img.src);
    }

    if (img.complete && img.naturalWidth > 0) {
      try {
        const texture = PIXI.Texture.from(img);
        this.textures.set(img.src, texture);
        return texture;
      } catch (e) {
        pixiRendererLogger.warn('Failed to create texture from image:', img.src);
        return null;
      }
    }

    return null;
  },

  /**
   * Get texture for a specific animation frame (cropped from spritesheet)
   * @param {{image: HTMLImageElement, frame: {x:number,y:number,w:number,h:number}, cacheKey?: string}} renderable
   * @returns {PIXI.Texture|null}
   */
  getTextureFromFrame(renderable) {
    if (!renderable || !renderable.image || !renderable.frame) {
      return null;
    }

    const baseTexture = this.getTextureFromImage(renderable.image);
    if (!baseTexture) {
      return null;
    }

    const { frame } = renderable;
    const cacheKey = renderable.cacheKey
      ? `anim:${renderable.cacheKey}`
      : `anim:${renderable.image.src}|${frame.x},${frame.y},${frame.w},${frame.h}`;

    if (this.textures.has(cacheKey)) {
      return this.textures.get(cacheKey);
    }

    try {
      const texture = new PIXI.Texture({
        source: baseTexture.source,
        frame: new PIXI.Rectangle(frame.x, frame.y, frame.w, frame.h)
      });
      this.textures.set(cacheKey, texture);
      return texture;
    } catch (err) {
      pixiRendererLogger.warn('Failed to create frame texture:', cacheKey, err);
      return null;
    }
  },

  /**
   * Show/hide the PixiJS canvas
   * @param {boolean} visible - Whether to show the canvas
   */
  setVisible(visible) {
    if (this.app?.canvas) {
      this.app.canvas.style.display = visible ? 'block' : 'none';
    }
  },

  /**
   * Get current FPS
   * @returns {number}
   */
  getFPS() {
    return this.stats.fps;
  },

  /**
   * Get renderer type string
   * @returns {string}
   */
  getRendererType() {
    if (!this.app) return 'Not initialized';
    return this.app.renderer.type === 1 ? 'WebGL' : 'Canvas2D (fallback)';
  },

  /**
   * Get the canvas element
   * @returns {HTMLCanvasElement|null}
   */
  getCanvas() {
    return this.app?.canvas || null;
  },

  /**
   * Reset renderer state without destroying the PIXI app
   * Called when returning to lobby to clear cached sprites
   */
  reset() {
    // Reset sub-renderers (clear cached sprites, but keep containers)
    if (PixiEntityRenderer?.reset) PixiEntityRenderer.reset();
    if (PixiWorldRenderer?.reset) PixiWorldRenderer.reset();
    if (PixiEffectsRenderer?.reset) PixiEffectsRenderer.reset();
    if (PixiTrailRenderer?.reset) PixiTrailRenderer.reset();
    if (PixiContainerRenderer?.reset) PixiContainerRenderer.reset();
    // HUD doesn't need reset - elements are recreated per-game

    this._wallsDrawn = false;
  },

  /**
   * Clean up resources
   */
  destroy() {
    // Remove event listeners
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    if (this.app?.canvas) {
      if (this._contextLostHandler) {
        this.app.canvas.removeEventListener('webglcontextlost', this._contextLostHandler);
        this._contextLostHandler = null;
      }
      if (this._contextRestoredHandler) {
        this.app.canvas.removeEventListener('webglcontextrestored', this._contextRestoredHandler);
        this._contextRestoredHandler = null;
      }
    }

    // Destroy sub-renderers
    if (PixiWorldRenderer?.destroy) PixiWorldRenderer.destroy();
    if (PixiEntityRenderer?.destroy) PixiEntityRenderer.destroy();
    if (PixiEffectsRenderer?.destroy) PixiEffectsRenderer.destroy();
    if (PixiTrailRenderer?.destroy) PixiTrailRenderer.destroy();
    if (PixiContainerRenderer?.destroy) PixiContainerRenderer.destroy();
    if (PixiHUDRenderer?.destroy) PixiHUDRenderer.destroy();

    for (const texture of this.textures.values()) {
      texture.destroy(true);
    }
    this.textures.clear();

    if (this.app) {
      this.app.destroy(true);
      this.app = null;
    }

    this.initialized = false;
    this.contextLost = false;
    this._wallsDrawn = false;
  },

  /**
   * Set Tiled map data for world rendering
   * @param {Object} tiledData - Loaded Tiled map data from TiledMapLoader
   */
  setTiledData(tiledData) {
    if (PixiWorldRenderer?.setTiledData) {
      PixiWorldRenderer.setTiledData(tiledData);
    }

    // Update background color based on map
    // You can customize this logic based on map name or metadata
    if (tiledData && this.app) {
      // Maps without tile layers are flat-shape worlds: the canvas is the
      // out-of-bounds grass; the playable interior is painted separately.
      const flatWorld = !tiledData.tileLayers || tiledData.tileLayers.length === 0;
      const bgColor = flatWorld ? getWorldTheme().groundOutside : 0x1a4d2e;
      if (flatWorld && PixiWorldRenderer) {
        PixiWorldRenderer.gridColor = getWorldTheme().groundGrid;
        PixiWorldRenderer.gridAlpha = 1;
        PixiWorldRenderer.wallColor = getWorldTheme().boundary;
      }

      this.currentMapBackgroundColor = bgColor;
      this.app.renderer.background.color = bgColor;
    }
  },

  /**
   * Redraw boundary walls with new dimensions
   * @param {number} mapWidth - New map width
   * @param {number} mapHeight - New map height
   */
  redrawWalls(mapWidth, mapHeight) {
    // Reset the flag so walls get redrawn
    this._wallsDrawn = false;
    
    if (PixiWorldRenderer?.drawWalls) {
      PixiWorldRenderer.drawWalls(mapWidth, mapHeight);
      this._wallsDrawn = true;
    }
  }
};

// Export for use in other modules

// Expose to global so client code can access PixiRenderer

export { pixiRendererLoggerFallback, pixiRendererLogger, PixiRenderer };
